/* Use native section rendering: Shopify owns all cart prices and discounts. */
if (!customElements.get('levee-cart-pairs')) {
  class LeveeCartPairs extends HTMLElement {
    static recommendations = new Map();

    connectedCallback() {
      this.onSubmit = this.submitPair.bind(this);
      this.onChange = (event) => {
        if (!event.target.matches('[data-pair-variant]')) return;
        const form = event.target.closest('form');
        form.querySelector('[data-pair-price]').textContent = event.target.selectedOptions[0].dataset.price;
        // Keep the recovery link visible until the uncertain cart is checked.
        if (!this.uncertain) form.querySelector('[data-pair-error]').hidden = true;
      };
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);
      this.connectRecommendations();
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('focusin', this.onInteraction);
      this.removeEventListener('pointerdown', this.onInteraction);
      this.removeEventListener('change', this.onInteraction);
      this.recommendationDrawer?.removeEventListener('open', this.onDrawerOpen);
      this.releaseRecommendations();
    }

    connectRecommendations() {
      this.list = this.querySelector('[data-pairs-list]');
      this.catalog = this.querySelector('[data-pairs-catalog]');
      if (!this.list || !this.catalog || !this.dataset.recommendationsUrl) return;
      this.recommendationDrawer = this.closest('drawer-component');
      this.onInteraction = () => {
        this.interacted = true;
        this.releaseRecommendations();
      };
      this.addEventListener('focusin', this.onInteraction);
      this.addEventListener('pointerdown', this.onInteraction, { passive: true });
      this.addEventListener('change', this.onInteraction);
      this.onDrawerOpen = () => this.loadRecommendations();
      this.recommendationDrawer?.addEventListener('open', this.onDrawerOpen);
      if (this.recommendationDrawer?.isOpen()) this.loadRecommendations();
    }

    releaseRecommendations() {
      const entry = this.recommendationEntry;
      if (!entry) return;
      entry.consumers.delete(this);
      this.recommendationEntry = null;
      // A replacement element can reuse the same in-flight read in this render turn.
      setTimeout(() => {
        if (!entry.settled && entry.consumers.size === 0) {
          entry.controller.abort();
          if (LeveeCartPairs.recommendations.get(entry.key) === entry) LeveeCartPairs.recommendations.delete(entry.key);
        }
      }, 0);
    }

    async loadRecommendations() {
      if (!this.isConnected || this.interacted || this.rankingStarted || !this.recommendationDrawer?.isOpen()) return;
      const queue = window.FoxThemeCartMutations;
      if (queue?.pending) {
        if (!this.waitingForCart) {
          this.waitingForCart = true;
          queue.whenUnlocked(() => { this.waitingForCart = false; this.loadRecommendations(); });
        }
        return;
      }
      this.rankingStarted = true;
      try {
        const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
        if (url.origin !== window.location.origin) return;
        const key = url.href + '|' + (this.dataset.currency || window.Shopify?.currency?.active || '');
        const now = Date.now();
        for (const [cachedKey, cached] of LeveeCartPairs.recommendations) {
          if (cached.settled && cached.expires <= now) LeveeCartPairs.recommendations.delete(cachedKey);
        }
        let entry = LeveeCartPairs.recommendations.get(key);
        if (!entry) {
          entry = { key, consumers: new Set(), controller: new AbortController(), settled: false, expires: now + 300000 };
          entry.promise = fetch(url.href, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: entry.controller.signal })
            .then((response) => { if (!response.ok) throw new Error('Recommendations unavailable'); return response.json(); })
            .then((result) => Array.isArray(result.products) ? result.products.map((product) => String(product.id)) : [])
            .catch(() => [])
            .finally(() => { entry.settled = true; });
          LeveeCartPairs.recommendations.set(key, entry);
        }
        this.recommendationEntry = entry;
        entry.consumers.add(this);
        const ids = await entry.promise;
        if (!this.isConnected || this.interacted || this.recommendationEntry !== entry) return;
        const apply = () => {
          if (!this.isConnected || this.interacted || this.busy || LeveeCartPairs.pending || !this.recommendationDrawer?.isOpen()) return;
          if (queue?.pending) { queue.whenUnlocked(apply); return; }
          this.applyRecommendations(ids);
        };
        queue?.pending ? queue.whenUnlocked(apply) : apply();
      } catch (_) {
        // Server-rendered rows stay usable when related recommendations are unavailable.
      } finally {
        this.releaseRecommendations();
        this.rankingStarted = false;
      }
    }

    applyRecommendations(ids) {
      if (!ids.length) return;
      const catalog = Array.from(this.catalog.content.querySelectorAll('[data-pair-form][data-product-id]'));
      const byId = new Map(catalog.map((form) => [form.dataset.productId, form]));
      const ordered = [];
      const selected = new Set();
      for (const id of [...ids, ...catalog.map((form) => form.dataset.productId)]) {
        if (!byId.has(id) || selected.has(id)) continue;
        selected.add(id);
        ordered.push(byId.get(id));
        if (ordered.length === 3) break;
      }
      if (!ordered.length) return;
      const existing = Array.from(this.list.querySelectorAll('[data-pair-form]'));
      if (ordered.length === existing.length && ordered.every((form, index) => form.dataset.productId === existing[index].dataset.productId)) return;
      // All names, prices, variant availability and controls come from Shopify Liquid.
      this.list.replaceChildren(...ordered.map((form) => form.cloneNode(true)));
    }

    async submitPair(event) {
      const form = event.target.closest('[data-pair-form]');
      if (!form) return;
      event.preventDefault();
      if (LeveeCartPairs.pending || this.busy || !form.reportValidity()) return;
      const drawer = this.closest('cart-drawer');
      if (!drawer) return;
      const selector = form.querySelector('select');
      const selectorId = selector.id;
      const productName = form.querySelector('.levee-cart-pair__name').textContent;
      const size = selector.selectedOptions[0].textContent.trim();
      const scrollTop = drawer.querySelector('.f-drawer__scroller')?.scrollTop;
      this.interacted = true;
      // Snapshot the intended variant before a flushed quantity update rerenders it.
      const body = new FormData(form);
      body.set('sections', 'cart-drawer');
      body.set('sections_url', window.location.pathname);
      const action = form.action;
      let added = false;
      let rejected = false;
      LeveeCartPairs.pending = true;
      this.busy = true;
      form.querySelector('[data-pair-error]').hidden = true;
      this.setAttribute('aria-busy', 'true');
      form.querySelector('[data-pair-add]').textContent = 'Adding…';
      const queue = window.FoxThemeCartMutations;
      if (queue && window.FoxThemeSettings.template === 'cart') {
        const sections = new Set(['cart-drawer']);
        queue.editors.forEach((editor) => editor.getSectionsToRender().forEach((section) => sections.add(section.section)));
        body.set('sections', Array.from(sections).join(','));
      }
      // Compatibility fallback is only needed when this asset is used without cart.js.
      const controls = queue ? [] : Array.from(drawer.querySelectorAll('button:not(.f-drawer__close):not([data-cart-continue]), input, select'));
      const previousDisabled = controls.map((control) => control.disabled);
      controls.forEach((control) => { control.disabled = true; });
      const currentForm = () => {
        const currentDrawer = document.querySelector('cart-drawer');
        return (selectorId && currentDrawer?.querySelector(`#${selectorId}`)?.closest('form')) || (form.isConnected !== false ? form : null);
      };
      try {
        // This flush is synchronous: already-entered quantities enter the queue first.
        queue?.flushQuantityChanges();
        const addPair = async () => {
          const response = await fetch(action, { method: 'POST', headers: { Accept: 'application/json' }, body });
          const state = await response.json();
          if (!response.ok || state.status || state.errors) {
            rejected = true;
            throw new Error(typeof state.description === 'string' ? state.description : 'This pair could not be added. Please check your size and try again.');
          }
          added = true;
          // An accepted POST must never be repeated just because rendering failed.
          const parsed = state.sections?.['cart-drawer'] && new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
          if (!parsed?.querySelector('.f-cart-drawer__items') || !parsed.querySelector('.f-drawer__footer')) {
            window.location.assign(window.FoxThemeSettings.routes.cart_url || '/cart');
            return;
          }
          state.source = 'levee-cart-pairs';
          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, state);
        };
        await (queue ? queue.enqueue(addPair) : addPair());
        const restoreContext = () => {
          const currentDrawer = document.querySelector('cart-drawer');
          const status = currentDrawer?.querySelector('#CartDrawer-LiveRegionText');
          if (status) {
            status.setAttribute('aria-live', 'polite');
            status.textContent = productName + ', size ' + size + ', added to your bag.';
          }
          const focusTarget = currentDrawer?.querySelector('[data-pair-variant]') || currentDrawer?.querySelector('[data-checkout-button]');
          // Closing the drawer during a request must not move focus back into it.
          if (!currentDrawer?.closest || currentDrawer.closest('drawer-component')?.isOpen()) focusTarget?.focus({ preventScroll: true });
          const scroller = currentDrawer?.querySelector('.f-drawer__scroller');
          if (scroller && Number.isFinite(scrollTop)) scroller.scrollTop = scrollTop;
        };
        queue ? queue.whenUnlocked(restoreContext) : restoreContext();
      } catch (failure) {
        if (added) {
          window.location.assign(window.FoxThemeSettings.routes.cart_url || '/cart');
          return;
        }
        const liveForm = currentForm();
        const error = liveForm?.querySelector('[data-pair-error]') || document.querySelector('#CartDrawer-CartErrors');
        const pairs = liveForm?.closest('levee-cart-pairs') || (this.isConnected ? this : null);
        if (!error || !pairs) {
          window.location.assign(window.FoxThemeSettings.routes.cart_url || '/cart');
          return;
        }
        error.textContent = !rejected
          ? 'We could not confirm the update. Open your bag to check before adding again.'
          : failure.message;
        if (!rejected) {
          const link = document.createElement('a');
          link.href = window.FoxThemeSettings.routes.cart_url || '/cart';
          link.textContent = ' Open bag';
          error.append(link);
          pairs.uncertain = true;
          pairs.busy = true;
        }
        error.hidden = false;
      } finally {
        LeveeCartPairs.pending = false;
        controls.forEach((control, index) => { if (control.isConnected) control.disabled = previousDisabled[index]; });
        const liveForm = currentForm();
        const pairs = liveForm?.closest('levee-cart-pairs') || (this.isConnected ? this : null);
        if (pairs && !added) {
          pairs.busy = Boolean(pairs.uncertain);
          pairs.removeAttribute('aria-busy');
          pairs.querySelectorAll('[data-pair-add]').forEach((el) => { el.disabled = pairs.busy; });
          const button = liveForm?.querySelector('[data-pair-add]');
          if (button) button.textContent = 'Add +';
        }
      }
    }
  }
  customElements.define('levee-cart-pairs', LeveeCartPairs);
}
